import { useState } from "react";
import { ClipboardList, Loader2, Download, RotateCcw, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const STATUS_OPTIONS = ["Not Started", "In Progress", "In Review", "Done", "Blocked", "Deferred"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

const STATUS_COLORS = {
  "Not Started": { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
  "In Progress": { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  "In Review": { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  "Done": { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  "Blocked": { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  "Deferred": { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-400" },
};

const PRIORITY_COLORS = {
  "High": { bg: "bg-red-50 border-red-200", text: "text-red-700" },
  "Medium": { bg: "bg-amber-50 border-amber-200", text: "text-amber-700" },
  "Low": { bg: "bg-green-50 border-green-200", text: "text-green-700" },
};

const EXAMPLE_REQUIREMENTS = `User Authentication
- Login with email and password
- Social login (Google, Facebook)
- Password reset via email
- Two-factor authentication
- Session management

Dashboard
- Display key metrics (revenue, users, orders)
- Date range filter
- Export reports to PDF
- Real-time notifications

Product Management
- Add/edit/delete products
- Bulk upload via CSV
- Image gallery for each product
- Inventory tracking
- Price history`;

export default function TrackerGenerator() {
  const [requirements, setRequirements] = useState("");
  const [projectName, setProjectName] = useState("");
  const [includePriority, setIncludePriority] = useState(true);
  const [includeDates, setIncludeDates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleGenerate = async () => {
    if (!requirements.trim()) return;
    setLoading(true);
    setError("");
    setItems([]);
    setDownloadUrl(null);

    try {
      const res = await axios.post(`${API}/tracker/parse`, {
        requirements: requirements.trim(),
        project_name: projectName.trim(),
        include_priority: includePriority,
        include_dates: includeDates,
      });
      setItems(res.data.items || []);
    } catch (err) {
      setError("Failed to generate tracker. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!items.length) return;
    setDownloading(true);
    setDownloadUrl(null);

    try {
      const res = await axios.post(`${API}/tracker/download`, {
        items,
        project_name: projectName.trim(),
      }, { responseType: "blob" });

      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      setDownloadUrl(url);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${projectName || 'tracker'}_requirements.xlsx`.replace(/\s+/g, '_'));
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError("Download failed. Use the manual link below.");
    } finally {
      setDownloading(false);
    }
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleReset = () => {
    setRequirements("");
    setProjectName("");
    setItems([]);
    setError("");
    setDownloadUrl(null);
    setCollapsedCategories({});
  };

  // Group items by category
  const grouped = items.reduce((acc, item, idx) => {
    const cat = item.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...item, _idx: idx });
    return acc;
  }, {});

  const stats = {
    total: items.length,
    high: items.filter(i => i.priority === "High").length,
    done: items.filter(i => i.status === "Done").length,
    categories: Object.keys(grouped).length,
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#EDE9FE] flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <span className="label-mono">Tracker Generator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="tracker-page-title">
            Requirements to Tracker Sheet
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Paste your checklist or requirements — get an interactive tracker card with dropdowns for status, priority, and owner. Then download as Excel.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {/* Input Section (shows when no items) */}
        {items.length === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-6">
              <div>
                <label className="label-mono block mb-3">Project Name (optional)</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. E-Commerce Platform v2"
                  className="bg-slate-50 border-transparent focus:border-[#7C3AED] focus:bg-white rounded-md h-11"
                  data-testid="project-name-input"
                />
              </div>

              <div>
                <label className="label-mono block mb-3">Requirements / Checklist</label>
                <Textarea
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                  placeholder="Paste your requirements, checklist, or task list here..."
                  className="min-h-[280px] text-sm bg-slate-50 border-transparent focus:border-[#7C3AED] focus:bg-white rounded-md resize-none"
                  data-testid="requirements-input"
                />
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <Switch checked={includePriority} onCheckedChange={setIncludePriority} data-testid="priority-toggle" />
                  Auto-assign priorities
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <Switch checked={includeDates} onCheckedChange={setIncludeDates} data-testid="dates-toggle" />
                  Include due date column
                </label>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleGenerate}
                  disabled={loading || !requirements.trim()}
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md px-6 h-11 font-medium btn-press"
                  data-testid="generate-tracker-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><ClipboardList className="w-4 h-4 mr-2" />Generate Tracker</>
                  )}
                </Button>
              </div>

              {error && <p className="text-sm text-red-500" data-testid="tracker-error">{error}</p>}
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="border border-slate-200 rounded-lg bg-white p-6">
                <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>What you get</h3>
                <div className="space-y-3 text-sm text-slate-600">
                  <div className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-[#7C3AED] mt-1.5 flex-shrink-0" /><span>Interactive tracker card with editable fields</span></div>
                  <div className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0" /><span>Dropdown for Status: Not Started, In Progress, Done, Blocked...</span></div>
                  <div className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-[#0055FF] mt-1.5 flex-shrink-0" /><span>Dropdown for Priority: High, Medium, Low</span></div>
                  <div className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" /><span>Edit owner, due dates, notes inline</span></div>
                  <div className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" /><span>Download as styled Excel with your changes</span></div>
                </div>
              </div>

              <div className="border border-dashed border-slate-300 rounded-lg p-5">
                <span className="label-mono block mb-3">Try an example</span>
                <p className="text-xs text-slate-500 mb-3">Load sample requirements to see how the tracker works.</p>
                <Button
                  variant="outline"
                  onClick={() => { setRequirements(EXAMPLE_REQUIREMENTS); setProjectName("E-Commerce Platform"); }}
                  className="text-sm rounded-md border-slate-300 text-slate-600 hover:border-[#7C3AED] hover:text-[#7C3AED] btn-press"
                  data-testid="load-example-button"
                >
                  <ClipboardList className="w-4 h-4 mr-1.5" />Load Example
                </Button>
              </div>

              <AdUnit format="vertical" />
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED] mx-auto mb-4" />
              <p className="text-sm text-slate-600 font-medium">AI is parsing your requirements...</p>
              <p className="text-xs text-slate-400 mt-1">Categorizing, prioritizing, and structuring your tasks</p>
            </div>
          </div>
        )}

        {/* Interactive Tracker Card */}
        {items.length > 0 && !loading && (
          <div className="space-y-6">
            {/* Header bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="tracker-card-title">
                  {projectName || "Requirements Tracker"}
                </h2>
                <p className="text-sm text-slate-500 mt-1">Edit status, priority, and owner below — then download your customized Excel.</p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md px-5 h-10 font-medium btn-press"
                  data-testid="download-excel-button"
                >
                  {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Downloading...</> : <><Download className="w-4 h-4 mr-2" />Download Excel</>}
                </Button>
                <Button variant="outline" onClick={handleReset} className="rounded-md h-10 px-4 border-slate-300 text-slate-600 btn-press" data-testid="tracker-reset-button">
                  <RotateCcw className="w-4 h-4 mr-1.5" />New
                </Button>
              </div>
            </div>

            {downloadUrl && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="tracker-download-fallback">
                <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0" />
                <a href={downloadUrl} download={`${projectName || 'tracker'}_requirements.xlsx`.replace(/\s+/g, '_')} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#7C3AED] hover:underline" data-testid="manual-download-link">
                  Click here if download didn't start automatically
                </a>
              </div>
            )}

            {/* Stats bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tracker-stats">
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats.total}</p>
                <p className="text-xs text-slate-500 mt-0.5">Total Items</p>
              </div>
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats.high}</p>
                <p className="text-xs text-slate-500 mt-0.5">High Priority</p>
              </div>
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <p className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats.done}</p>
                <p className="text-xs text-slate-500 mt-0.5">Completed</p>
              </div>
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <p className="text-2xl font-bold text-[#7C3AED]" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats.categories}</p>
                <p className="text-xs text-slate-500 mt-0.5">Categories</p>
              </div>
            </div>

            {/* Ad */}
            <AdUnit format="leaderboard" />

            {/* Tracker table by category */}
            <div className="space-y-4">
              {Object.entries(grouped).map(([category, catItems]) => {
                const isCollapsed = collapsedCategories[category];
                const catDone = catItems.filter(i => i.status === "Done").length;
                return (
                  <div key={category} className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={`category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
                    {/* Category header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid={`toggle-${category.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3">
                        {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        <span className="font-bold text-sm text-slate-800" style={{ fontFamily: 'Chivo, sans-serif' }}>{category}</span>
                        <span className="text-xs text-slate-400 font-mono">{catItems.length} items</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${catItems.length ? (catDone / catItems.length) * 100 : 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-400 font-mono">{catDone}/{catItems.length}</span>
                      </div>
                    </button>

                    {/* Items */}
                    {!isCollapsed && (
                      <div className="divide-y divide-slate-100">
                        {/* Table header */}
                        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/50 text-xs font-mono uppercase tracking-wider text-slate-400">
                          <div className="col-span-1">#</div>
                          <div className="col-span-3">Task</div>
                          <div className="col-span-2">Priority</div>
                          <div className="col-span-2">Status</div>
                          <div className="col-span-2">Owner</div>
                          <div className="col-span-2">Due Date</div>
                        </div>

                        {catItems.map((item, localIdx) => {
                          const sc = STATUS_COLORS[item.status] || STATUS_COLORS["Not Started"];
                          const pc = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS["Medium"];
                          return (
                            <div
                              key={item._idx}
                              className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-slate-50/80 transition-colors ${item.status === "Done" ? "opacity-60" : ""}`}
                              data-testid={`tracker-row-${item._idx}`}
                            >
                              {/* # */}
                              <div className="col-span-1 text-xs font-mono text-slate-400">{item._idx + 1}</div>

                              {/* Task */}
                              <div className="col-span-3">
                                <p className={`text-sm text-slate-800 leading-snug ${item.status === "Done" ? "line-through" : ""}`}>
                                  {item.item}
                                </p>
                                {item.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{item.notes}</p>}
                              </div>

                              {/* Priority dropdown */}
                              <div className="col-span-2">
                                <Select value={item.priority} onValueChange={(v) => updateItem(item._idx, "priority", v)}>
                                  <SelectTrigger className={`h-8 text-xs font-medium border rounded-md ${pc.bg} ${pc.text}`} data-testid={`priority-select-${item._idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {PRIORITY_OPTIONS.map(p => (
                                      <SelectItem key={p} value={p}>
                                        <span className={PRIORITY_COLORS[p]?.text}>{p}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Status dropdown */}
                              <div className="col-span-2">
                                <Select value={item.status} onValueChange={(v) => updateItem(item._idx, "status", v)}>
                                  <SelectTrigger className={`h-8 text-xs font-medium rounded-md ${sc.bg} ${sc.text}`} data-testid={`status-select-${item._idx}`}>
                                    <div className="flex items-center gap-1.5">
                                      <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                      <SelectValue />
                                    </div>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STATUS_OPTIONS.map(s => {
                                      const sColors = STATUS_COLORS[s];
                                      return (
                                        <SelectItem key={s} value={s}>
                                          <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${sColors.dot}`} />
                                            {s}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Owner */}
                              <div className="col-span-2">
                                <Input
                                  value={item.owner || ""}
                                  onChange={(e) => updateItem(item._idx, "owner", e.target.value)}
                                  placeholder="Assign..."
                                  className="h-8 text-xs bg-transparent border-transparent hover:border-slate-200 focus:border-[#7C3AED] rounded-md"
                                  data-testid={`owner-input-${item._idx}`}
                                />
                              </div>

                              {/* Due Date */}
                              <div className="col-span-2">
                                <Input
                                  type="date"
                                  value={item.due_date || ""}
                                  onChange={(e) => updateItem(item._idx, "due_date", e.target.value)}
                                  className="h-8 text-xs bg-transparent border-transparent hover:border-slate-200 focus:border-[#7C3AED] rounded-md"
                                  data-testid={`date-input-${item._idx}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom download bar */}
            <div className="flex items-center justify-between border border-slate-200 rounded-lg bg-white p-4">
              <p className="text-sm text-slate-500">
                <span className="font-medium text-slate-700">{items.length} items</span> across {stats.categories} categories. Changes reflect in the downloaded Excel.
              </p>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md px-5 h-10 font-medium btn-press"
                data-testid="download-excel-bottom"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download Excel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
